import classNames from 'classnames';
import { useRef, useState, useEffect, memo } from 'react';
import { IMaskInput } from 'react-imask';
import { ReactElement, } from 'react-imask/dist/mixin';
import { FieldIcon } from '../../../enums/field-icon.enum';
import ReactDropdown from 'react-dropdown';
import 'react-dropdown/style.css';

import styles from './form-field.module.scss';

interface FormFieldProps {
  name: string;
  value: string;
  onChange: (element: HTMLInputElement & EventTarget | { name: string, value: any }) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onPaste?: (e: React.ClipboardEvent<ReactElement>) => void;
  onEnter?: () => void;
  doFocus?: boolean;
  label?: string;
  placeholder?: string;
  type?: string;
  mask?: string;
  hasError?: boolean
  autoComplete?: string;
  margin?: boolean;
  animateCursor?: boolean;
  customStyles?: Record<string, any>;
  formViewType?: "default" | "grid" | "variants";
  icon?: FieldIcon;
  options?: string[];
}

export const FormField = memo((props: FormFieldProps) => {
  const { label, hasError, margin, animateCursor, onFocus, onBlur, onPaste, doFocus, onChange, onEnter, customStyles, formViewType = "default", icon, ...rest } = props;
  const [focus, setFocus] = useState(false);
  const maskRef = useRef<{ element: HTMLInputElement }>(null);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (doFocus && maskRef.current) maskRef.current.element.focus();
    if (doFocus && ref.current) ref.current.focus();
  }, [doFocus, maskRef, ref]);

  const onFocusHandle = () => {
    setFocus(true);
    onFocus && onFocus();
  }

  const onBlurHandle = (e: React.FocusEvent<ReactElement>) => {
    if (e.currentTarget.value) setFocus(true)
    else setFocus(false);

    onBlur && onBlur();
  }

  const onAcceptHandle = (value: any) => {
    const element = maskRef.current?.element || { value, name: rest.name };
    onChange && onChange(element);
  }

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (maskRef.current) onChange(maskRef.current.element);
    else onChange(e.currentTarget);
  }

  const onKeyPressHandle = (e: React.KeyboardEvent<Element>) => {
    if (e.key == "Enter") onEnter && onEnter();
  }

  return <label
    htmlFor={rest.name}
    style={customStyles}
    className={classNames(
      styles[formViewType],
      styles[icon],
      {
        [styles.hasError]: hasError,
        [styles.margin]: margin,
        [styles.withLabel]: label,
        [styles.focus]: focus,
        [styles.animateCursor]: animateCursor,
        [styles.withIcon]: icon != undefined
      }
    )}>
    {hasError && <div className={styles.errorIcon}>!</div>}
    {label && <h3 className={styles.label}>{label}</h3>}

    {rest.mask ?
      <IMaskInput
        ref={maskRef}
        id={rest.name}
        name={rest.name}
        value={rest.value}
        mask={rest.mask}
        placeholder={rest.placeholder}
        placeholderChar={"_"}
        type={rest.type}
        lazy={false}
        autoComplete={rest.autoComplete}
        onAccept={onAcceptHandle}
        onFocus={onFocusHandle}
        onBlur={onBlurHandle}
        onPaste={onPaste}
      />
      : (rest.type == "select" ?
        <ReactDropdown
          controlClassName={styles.dropdownControl}
          options={rest.options}
          placeholder={rest.placeholder}
          placeholderClassName={styles.dropdownPlaceholder}
          className={styles.dropdownRoot}
          menuClassName={styles.dropdownMenu}
          value={rest.value}
          onChange={(arg) => onChange({ name: rest.name, value: arg.value })}
          arrowClassName={styles.dropdownArrow}
        /> : <input
          ref={ref}
          id={rest.name}
          name={rest.name}
          value={rest.value}
          placeholder={rest.placeholder}
          type={rest.type}
          autoComplete={rest.autoComplete}
          onChange={onChangeHandler}
          contentEditable={true}
          readOnly={false}
          onKeyPress={onKeyPressHandle}
          onFocus={onFocusHandle}
          onBlur={onBlurHandle}
          onPaste={onPaste}
        />)
    }
  </label>
});